/**
 * Sends a completed diagnostic to the backend.
 *
 * Lives here rather than inside a component because TWO different moments
 * need to trigger it and they cannot share a React effect:
 *
 *   1. Already signed in. The run finishes while authenticated, so nothing
 *      about the auth session changes and an effect keyed on it never
 *      re-fires. Without an explicit call here the diagnostic would sit in
 *      localStorage until the user happened to navigate.
 *   2. Signed up afterwards. Auth resolves later, possibly on a different
 *      route after an OAuth round trip, which is what DiagnosticFlush
 *      watches for.
 *
 * Safe to call from both. The backend replaces rather than appends, so a
 * double call cannot duplicate rows.
 */

import { authFetch } from "./authFetch";
import { API_BASE } from "./api";
import { DIAGNOSTIC_QUESTIONS, toLogPayload } from "./diagnosticQuestions";
import { clearSession, loadSession } from "./diagnosticSession";

export type FlushOutcome =
  | { status: "saved"; saved: number; replaced: number }
  | { status: "nothing-to-send" }
  | { status: "stale-set" }
  | { status: "rejected"; code: number }
  | { status: "retry-later" };

/** Guards a second attempt while the first is still in flight, e.g. an
 *  auth refresh and a completion firing at nearly the same moment. */
let inFlight = false;

export async function flushDiagnostic(): Promise<FlushOutcome> {
  if (inFlight) return { status: "retry-later" };

  const pending = loadSession();
  if (!pending) return { status: "nothing-to-send" };
  // Only a COMPLETE run is worth saving. A half-finished one stays local so
  // the student can still resume it.
  if (pending.answers.length < DIAGNOSTIC_QUESTIONS.length) {
    return { status: "nothing-to-send" };
  }

  const answers = pending.answers.map(toLogPayload).filter(Boolean);
  if (answers.length !== pending.answers.length) {
    // A question id no longer in the set means the run predates a content
    // change. Submitting a partial set would write a misleading record.
    clearSession();
    return { status: "stale-set" };
  }

  inFlight = true;
  try {
    const res = await authFetch(`${API_BASE}/questions/diagnostic`, {
      method: "POST",
      body: JSON.stringify({
        diagnostic_session_id: pending.diagnosticSessionId,
        question_set_version: pending.questionSetVersion,
        answers,
      }),
    });

    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      clearSession();
      return { status: "saved", saved: body.saved ?? answers.length, replaced: body.replaced ?? 0 };
    }

    // A 4xx will never succeed with this payload, so retrying forever just
    // means the run is stuck in storage. Drop it rather than loop.
    if (res.status >= 400 && res.status < 500) {
      clearSession();
      return { status: "rejected", code: res.status };
    }

    return { status: "retry-later" };
  } catch {
    // Network failure. Leave it in place and try again on the next load.
    return { status: "retry-later" };
  } finally {
    inFlight = false;
  }
}
